'use client';

import React from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { FieldRow } from '@innovixx/react-form-fields';
import axios from 'axios';
import { Button } from '../../../Button';
import styles from './styles.module.scss';
import { AppSelectInput, AppTextInput } from '../../../formFields';
import { useToast } from '../../../../providers/ToastProviders';

interface Props {
  control: Control;
  handleNext: () => void;
  handlePrevious: () => void;
  leadFormId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: (name: string, value: any) => void;
}

export const AddressForm: React.FC<Props> = ({ control, handleNext, leadFormId, setValue, handlePrevious }) => {
  const [showAdditionalFields, setShowAdditionalFields] = React.useState(false);
  const [lookupPostcodeLoading, setLookupPostcodeLoading] = React.useState(false);

  const postcode = useWatch({ name: 'postcode', control });
  const { toast } = useToast();

  const handlePostcodeLookup = async () => {
    setLookupPostcodeLoading(true);
    try {
      const address = await axios.post(`${process.env.NEXT_PUBLIC_DEV_CLIENT_URL || process.env.NEXT_PUBLIC_CLIENT_URL}/api/lead-form/get-address`, {
        postcode,
        leadFormId,
      });

      if (address.data?.street) {
        setValue('addressLine1', address.data.street);
        setValue('city', address.data.locality);
        setValue('county', address.data.administrativeDivision);
        setValue('country', 'United Kingdom');
        setShowAdditionalFields(true);
      } else {
        toast.error({
          message: 'Postcode not found',
        });
        setShowAdditionalFields(true);
      }
    } catch {
      toast.error({
        message: 'Postcode not found',
      });
      setShowAdditionalFields(true);
    }
    setLookupPostcodeLoading(false);
  };

  return (
    <div className={styles.container}>
      {!showAdditionalFields && (
        <div>
          <legend className={styles.label}>Enter your details</legend>
          <div className={styles.postcodeLookupContainer}>
            <AppTextInput
              classNames={{
                container: styles.houseNumberInput,
                error: styles.postCodeErrorMessage,
              }}
              control={control}
              name="houseNameNumber"
              label="House Name/Number"
              required
            />
            <div className={styles.searchInputContainer}>
              <AppTextInput
                classNames={{
                  container: styles.postcodeInput,
                  error: styles.postCodeErrorMessage,
                }}
                control={control}
                name="postcode"
                label="Postcode"
                required
              />
              <Button
                className={styles.searchPostcodeButton}
                title="Search"
                onClick={handlePostcodeLookup}
                loading={lookupPostcodeLoading}
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      )}
      {showAdditionalFields && (
        <div>
          <h1 className={styles.label}>Enter your home address</h1>
          <div>
            <FieldRow>
              <AppTextInput
                control={control}
                name="houseNameNumber"
                label="House Name/Number"
                width="35%"
                required
              />
              <AppTextInput
                control={control}
                name="addressLine1"
                label="Address Line 1"
                required
              />
            </FieldRow>
            <FieldRow>
              <AppTextInput
                control={control}
                name="city"
                label="City"
                required
              />
              <AppTextInput
                control={control}
                name="county"
                label="County"
                required
              />
            </FieldRow>
            <FieldRow>
              <AppTextInput
                control={control}
                name="postcode"
                label="Postcode"
                required
                width={50}
              />
              <AppSelectInput
                control={control}
                name="country"
                label="Country"
                options={[
                  {
                    label: 'United Kingdom',
                    value: 'United Kingdom',
                  },
                ]}
                required
                width={50}
              />
            </FieldRow>
          </div>
        </div>
      )}
      <div className={styles.buttonsContainer}>
        <Button
          title="Back"
          onClick={handlePrevious}
          type='secondary'
        >
          Back
        </Button>
        <Button
          title="Next"
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
};
